import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { PaginatedList } from "libs/models/paginated-list";
import { EqualFilter, Filter } from "libs/models/filter";
import { FilterTab } from "components/tab-filter/tab-filter.props";
import { AsyncAppThunk } from "store/application-state";
import { addSnack, showLoaderWhile } from "./ui";
import { getFilterQuery } from "utils/filter";
import {
  addBatchService,
  downloadFileByIdService,
  editBatchStatusService,
  exportBatchCardsService,
  getBatchDetailsService,
  getBatchesService,
  getProductItemsByIdService,
} from "services/crosspay-service";
import {
  BatchModel,
  BatchRequestModel,
  ProductItemModel,
} from "libs/models/batch-model";
import { validateAddBatchState } from "pages/AddBatch/add-batch.policy";
import { setCloseDialog } from "./modal-popup";
import { productItemFilterTabs } from "pages/BatchDetailsView/batch-details.util";
import translate from "utils/translations";
import { downloadFileById } from "./client";

export interface BatchStore {
  isValidating: boolean;
  newBatchData: BatchRequestModel;
  batchesData: PaginatedList<BatchModel>;
  filter: Filter;
  batchesSearchValue: string | undefined;
  searchingValue: string | undefined;
  tabFilterValue: FilterTab;
  isDialogOpen: boolean;
  batchDetails: BatchModel;
  productItemsData: PaginatedList<ProductItemModel>;
  productItemsFilter: Filter;
}

const initialState: BatchStore = {
  isValidating: false,
  isDialogOpen: false,
  newBatchData: {} as BatchRequestModel,
  batchesData: { list: [], pageSize: 10, pageIndex: 0, totalCount: 0 },
  filter: {},
  searchingValue: undefined,
  batchesSearchValue: undefined,
  tabFilterValue: productItemFilterTabs[0],
  productItemsData: { list: [], pageSize: 10, pageIndex: 0, totalCount: 0 },
  productItemsFilter: {
    orderBy: { key: "Balance", sort: "asc" },
  },
  batchDetails: {} as BatchModel,
};

const slice = createSlice({
  name: "batch",
  initialState,
  reducers: {
    setBatchFetchingData: (
      state: BatchStore,
      action: PayloadAction<PaginatedList<BatchModel>>,
    ) => {
      state.batchesData = action.payload;
    },
    setIsValidating: (state: BatchStore, action: PayloadAction<boolean>) => {
      state.isValidating = action.payload;
    },
    setEqualFilter: (
      state: BatchStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      if (value === null) {
        delete state.filter[key];
        return state;
      } else {
        state.filter = { ...state.filter, [key]: value! };
      }
      return state;
    },
    setProductItemsFetchingData: (
      state: BatchStore,
      action: PayloadAction<PaginatedList<ProductItemModel>>,
    ) => {
      state.productItemsData = action.payload;
    },
    setProductItemsPage: (state: BatchStore, action: PayloadAction<number>) => {
      state.productItemsData.pageIndex = action.payload;
    },
    setBatchViewDynamicSearchFilter: (
      state: BatchStore,
      action: PayloadAction<EqualFilter[]>,
    ) => {
      state.filter = { ...state.filter, or: action.payload };
    },
    setSearchKey: (state: BatchStore, action: PayloadAction<string>) => {
      state.searchingValue = action.payload;
    },
    setBatchSearchKey: (state: BatchStore, action: PayloadAction<string>) => {
      state.batchesSearchValue = action.payload;
    },
    setBatchPage: (state: BatchStore, action: PayloadAction<number>) => {
      state.batchesData.pageIndex = action.payload;
    },
    setProductItemsDynamicSearchFilter: (
      state: BatchStore,
      action: PayloadAction<EqualFilter[]>,
    ) => {
      state.productItemsFilter = {
        ...state.productItemsFilter,
        or: action.payload,
      };
    },
    setProductItemsStatusFilter: (
      state: BatchStore,
      action: PayloadAction<string>,
    ) => {
      state.productItemsFilter = {
        ...state.productItemsFilter,
        or: [{ Status: action.payload }],
      };
    },
    setNewBatchDataProps: (
      state: BatchStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      return {
        ...state,
        newBatchData: { ...state.newBatchData, [key]: value! },
      };
    },
    setTabFilterChange: (
      state: BatchStore,
      action: PayloadAction<FilterTab>,
    ) => {
      state.tabFilterValue = action.payload;
    },
    setBatchDetails: (state: BatchStore, action: PayloadAction<BatchModel>) => {
      state.batchDetails = action.payload;
    },

    addingBatch: (state: BatchStore) => {
      state.newBatchData = initialState.newBatchData;
    },
    clearFilters: (state: BatchStore) => {
      state.filter = initialState.filter;
      state.searchingValue = undefined;
    },
  },
});

export const {
  setBatchFetchingData,
  setEqualFilter,
  setBatchViewDynamicSearchFilter,
  setSearchKey,
  setTabFilterChange,
  setBatchPage,
  setBatchDetails,
  addingBatch,
  setIsValidating,
  setNewBatchDataProps,
  clearFilters,
  setProductItemsFetchingData,
  setProductItemsPage,
  setProductItemsDynamicSearchFilter,
  setProductItemsStatusFilter,
  setBatchSearchKey,
} = slice.actions;
export const reducer = slice.reducer;

export const addBatch = (): AsyncAppThunk => async (dispatch, store) => {
  var batchState = store().batch;
  dispatch(setIsValidating(true));
  if (Object.keys(validateAddBatchState(store().batch)).length === 0) {
    dispatch(
      showLoaderWhile(async () => {
        dispatch(setIsValidating(false));
        const response = await addBatchService(batchState.newBatchData);
        if (
          response &&
          Object.keys(response).length > 0 &&
          response.result !== null
        ) {
          dispatch(addingBatch());
          dispatch(getBatchesData(batchState.newBatchData.productId));
          dispatch(setCloseDialog());
          dispatch(
            addSnack({
              message: translate(
                "Batch_Added_Successfully",
                "Batch Added Successfully",
              ),
            }),
          );
        }
      }),
    );
  }
};
export const getProductItemsById =
  (id: string): AsyncAppThunk =>
  async (dispatch, store) => {
    dispatch(
      showLoaderWhile(async () => {
        var filterWithStatus = {
          ...store().batch.productItemsFilter,
          Status:
            store().batch.tabFilterValue?.id === 2
              ? "Active"
              : store().batch.tabFilterValue?.id === 3
                ? "Archived"
                : undefined,
        };
        var productItemsPaging = store().batch.productItemsData;
        var queryOptions = getFilterQuery(
          filterWithStatus,
          productItemsPaging.pageIndex * productItemsPaging.pageSize,
          productItemsPaging.pageSize,
        );
        const apiResponse = await getProductItemsByIdService(id, queryOptions);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setProductItemsFetchingData(apiResponse.result));
        }
      }),
    );
  };

export const getBatchesData =
  (id: string): AsyncAppThunk =>
  async (dispatch, store) => {
    dispatch(
      showLoaderWhile(async () => {
        var userPaging = store().batch.batchesData;
        var queryOptions = getFilterQuery(
          store().batch.filter,
          userPaging.pageIndex * userPaging.pageSize,
          userPaging.pageSize,
        );
        const apiResponse = await getBatchesService(id, queryOptions);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setBatchFetchingData(apiResponse.result));
        }
      }),
    );
  };

export const getBatchDetails =
  (id: string): AsyncAppThunk =>
  async (dispatch, store) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await getBatchDetailsService(id);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setBatchDetails(apiResponse.result));
        }
      }),
    );
  };

export const editBatchStatus =
  (status: number): AsyncAppThunk =>
  async (dispatch, store) => {
    const batchDetails = store().batch.batchDetails;
    const batchRequest = { id: batchDetails.id, status: status };
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await editBatchStatusService(batchRequest);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setBatchDetails({ ...batchDetails, batchStatus: status }));
          dispatch(setCloseDialog());
          dispatch(
            addSnack({
              message: translate(
                "Batch_Edited_Successfully",
                "Batch Edited Successfully",
              ),
            }),
          );
        }
      }),
    );
  };

export const exportBatch =
  (id: string): AsyncAppThunk =>
  async (dispatch) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await exportBatchCardsService(id);
        if (apiResponse && Object.keys(apiResponse).length > 0) {
          dispatch(
            addSnack({
              message: translate(
                "File_Downloaded_Text",
                "File Downloaded successfully",
              ),
            }),
          );
        }
      }),
    );
  };
